/**
 * Alert Component
 *
 * Componente de alerta reutilizável com suporte a diferentes tipos,
 * variantes visuais, ícones, dismissible e ações customizadas.
 * Suporta dark mode e internacionalização.
 *
 * @module components/ui
 * @since 2025-12-16
 * @updated 2025-12-16 - Implementado i18n com react-i18next
 * @updated 2026-04-06 - Migrado para shadcn/ui (CVA + cn() + lucide-react)
 * @epic INIT-0046 Epic 0002
 *
 * @example
 * // Alerta de sucesso básico
 * <Alert type="success">Operação realizada com sucesso!</Alert>
 *
 * @example
 * // Alerta de erro com título
 * <Alert type="error" title="Erro ao salvar">
 *   Verifique os campos e tente novamente.
 * </Alert>
 *
 * @example
 * // Alerta dismissible (pode ser fechado)
 * <Alert type="warning" dismissible onDismiss={() => setShowAlert(false)}>
 *   Sua sessão expira em 5 minutos.
 * </Alert>
 *
 * @example
 * // Alerta com ação customizada
 * <Alert
 *   type="info"
 *   title="Nova versão disponível"
 *   action={<Button size="sm" onClick={handleUpdate}>Atualizar</Button>}
 * >
 *   Uma nova versão do sistema está disponível.
 * </Alert>
 *
 * @example
 * // Usando presets para código mais limpo
 * <SuccessAlert>Dados salvos com sucesso!</SuccessAlert>
 * <ErrorAlert title="Erro">Falha na conexão.</ErrorAlert>
 */

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { CircleCheck, CircleAlert, TriangleAlert, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTmfTranslation } from '@/i18n';

// ==========================================
// TYPES
// ==========================================

/** Tipos de alerta disponíveis */
export type AlertType = 'success' | 'error' | 'warning' | 'info';

/** Variantes visuais do alerta */
export type AlertVariant = 'filled' | 'outlined' | 'light';

// ==========================================
// CVA VARIANTS
// ==========================================

const alertVariants = cva(
  'relative w-full rounded-lg p-4',
  {
    variants: {
      type: {
        success: '',
        error: '',
        warning: '',
        info: '',
      },
      variant: {
        filled: '',
        outlined: 'border-2 bg-white dark:bg-black/20',
        light: 'border',
      },
    },
    compoundVariants: [
      // success
      { type: 'success', variant: 'filled', class: 'bg-green-600 text-white' },
      { type: 'success', variant: 'outlined', class: 'border-green-600 text-green-700 dark:text-green-400' },
      { type: 'success', variant: 'light', class: 'bg-green-50 dark:bg-green-500/10 text-green-800 dark:text-green-300 border-green-200 dark:border-green-500/30' },
      // error
      { type: 'error', variant: 'filled', class: 'bg-red-600 text-white' },
      { type: 'error', variant: 'outlined', class: 'border-red-600 text-red-700 dark:text-red-400' },
      { type: 'error', variant: 'light', class: 'bg-red-50 dark:bg-red-500/10 text-red-800 dark:text-red-300 border-red-200 dark:border-red-500/30' },
      // warning
      { type: 'warning', variant: 'filled', class: 'bg-yellow-500 text-white' },
      { type: 'warning', variant: 'outlined', class: 'border-yellow-500 text-yellow-700 dark:text-yellow-400' },
      { type: 'warning', variant: 'light', class: 'bg-yellow-50 dark:bg-yellow-500/10 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-500/30' },
      // info
      { type: 'info', variant: 'filled', class: 'bg-blue-600 text-white' },
      { type: 'info', variant: 'outlined', class: 'border-blue-600 text-blue-700 dark:text-blue-300' },
      { type: 'info', variant: 'light', class: 'bg-blue-50 dark:bg-blue-500/10 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-500/30' },
    ],
    defaultVariants: {
      type: 'info',
      variant: 'light',
    },
  }
);

const iconVariants = cva(
  'flex-shrink-0 w-5 h-5',
  {
    variants: {
      type: {
        success: '',
        error: '',
        warning: '',
        info: '',
      },
      variant: {
        filled: 'text-white',
        outlined: '',
        light: '',
      },
    },
    compoundVariants: [
      { type: 'success', variant: 'outlined', class: 'text-green-600 dark:text-green-400' },
      { type: 'success', variant: 'light', class: 'text-green-500 dark:text-green-400' },
      { type: 'error', variant: 'outlined', class: 'text-red-600 dark:text-red-400' },
      { type: 'error', variant: 'light', class: 'text-red-500 dark:text-red-400' },
      { type: 'warning', variant: 'outlined', class: 'text-yellow-600 dark:text-yellow-400' },
      { type: 'warning', variant: 'light', class: 'text-yellow-500 dark:text-yellow-400' },
      { type: 'info', variant: 'outlined', class: 'text-foreground/80' },
      { type: 'info', variant: 'light', class: 'text-blue-500 dark:text-blue-300' },
    ],
    defaultVariants: {
      type: 'info',
      variant: 'light',
    },
  }
);

// ==========================================
// DEFAULT ICONS
// ==========================================

const defaultIconMap: Record<AlertType, React.ElementType> = {
  success: CircleCheck,
  error: CircleAlert,
  warning: TriangleAlert,
  info: Info,
};

// ==========================================
// PROPS
// ==========================================

/**
 * Props do componente Alert
 */
export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  /** Tipo do alerta que define cor e ícone padrão @default 'info' */
  type?: AlertType;
  /** Variante visual: filled (fundo sólido), outlined (borda), light (fundo claro) @default 'light' */
  variant?: AlertVariant;
  /** Título destacado do alerta */
  title?: string;
  /** Conteúdo/mensagem do alerta */
  children: React.ReactNode;
  /** Ícone customizado (substitui o ícone padrão do tipo) */
  icon?: React.ReactNode;
  /** Se true, exibe o ícone @default true */
  showIcon?: boolean;
  /** Se true, exibe botão X para fechar o alerta @default false */
  dismissible?: boolean;
  /** Callback chamado quando o alerta é fechado */
  onDismiss?: () => void;
  /** Elemento de ação (botão, link) exibido à direita */
  action?: React.ReactNode;
}

// ==========================================
// COMPONENT
// ==========================================

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      type = 'info',
      variant = 'light',
      title,
      children,
      icon,
      showIcon = true,
      dismissible = false,
      onDismiss,
      action,
      className,
      ...props
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = React.useState(true);
    const { t } = useTmfTranslation('buttons');

    const handleDismiss = () => {
      setIsVisible(false);
      onDismiss?.();
    };

    if (!isVisible) return null;

    const IconComponent = defaultIconMap[type];
    const displayIcon = icon ?? <IconComponent className={cn(iconVariants({ type, variant }))} />;

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(alertVariants({ type, variant }), className)}
        {...props}
      >
        <div className="flex items-start gap-3">
          {/* Icon */}
          {showIcon && displayIcon && (
            <span className="flex-shrink-0">{displayIcon}</span>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            {title && (
              <p className="font-semibold text-sm mb-1">{title}</p>
            )}
            <div className={cn('text-sm', title && 'opacity-90')}>
              {children}
            </div>
          </div>

          {/* Action */}
          {action && (
            <div className="flex-shrink-0">
              {action}
            </div>
          )}

          {/* Dismiss button */}
          {dismissible && (
            <button
              onClick={handleDismiss}
              className={cn(
                'flex-shrink-0 p-1 rounded-lg transition-opacity',
                'hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-2',
                variant === 'filled' ? 'focus:ring-white' : 'focus:ring-current'
              )}
              aria-label={t('close')}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }
);

Alert.displayName = 'Alert';

// ==========================================
// PRESETS
// ==========================================

interface AlertPresetProps {
  title?: string;
  children: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

export function SuccessAlert(props: AlertPresetProps) {
  return <Alert type="success" {...props} />;
}

export function ErrorAlert(props: AlertPresetProps) {
  return <Alert type="error" {...props} />;
}

export function WarningAlert(props: AlertPresetProps) {
  return <Alert type="warning" {...props} />;
}

export function InfoAlert(props: AlertPresetProps) {
  return <Alert type="info" {...props} />;
}

// ==========================================
// INLINE ALERT (Compacta)
// ==========================================

const inlineIconVariants = cva('flex-shrink-0 w-5 h-5', {
  variants: {
    type: {
      success: 'text-green-600 dark:text-green-400',
      error: 'text-red-600 dark:text-red-400',
      warning: 'text-yellow-600 dark:text-yellow-400',
      info: 'text-foreground/80',
    },
  },
  defaultVariants: {
    type: 'info',
  },
});

const inlineTextVariants = cva('flex items-center gap-2 text-sm', {
  variants: {
    type: {
      success: 'text-green-600 dark:text-green-400',
      error: 'text-red-600 dark:text-red-400',
      warning: 'text-yellow-600 dark:text-yellow-400',
      info: 'text-foreground/80',
    },
  },
  defaultVariants: {
    type: 'info',
  },
});

interface InlineAlertProps {
  type?: AlertType;
  children: React.ReactNode;
  className?: string;
}

export function InlineAlert({ type = 'info', children, className }: InlineAlertProps) {
  const IconComponent = defaultIconMap[type];

  return (
    <div className={cn(inlineTextVariants({ type }), className)}>
      <IconComponent className={cn(inlineIconVariants({ type }))} />
      <span>{children}</span>
    </div>
  );
}
